import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const stats = [
  { value: 500, suffix: '+', label: 'Spaces Cleaned' },
  { value: 3, suffix: '+', label: 'Years of Excellence' },
  { value: 150, suffix: '+', label: 'Happy Clients' },
  { value: 3, suffix: '', label: 'States Served' },
];

const Counter = ({ to, suffix, duration = 2000 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = to / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= to) {
        setVal(to);
        clearInterval(timer);
      } else {
        setVal(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, to, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
};

export const Stats = () => {
  return (
    <section
      className="relative py-24 md:py-32 bg-[#0D1B2A] text-white overflow-hidden"
      data-testid="stats-section"
    >
      {/* Subtle diagonal texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, #ffffff 0, #ffffff 1px, transparent 1px, transparent 24px)',
        }}
      />
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-[#C9A84C]/15 blur-[140px] pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Centered editorial header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center mb-16 md:mb-20"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#C9A84C]" />
            <span className="text-[#C9A84C] text-[11px] tracking-[0.45em] uppercase font-semibold">
              By the Numbers
            </span>
            <div className="h-px w-10 bg-[#C9A84C]" />
          </div>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-4">
          {stats.map((s, idx) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
              className="text-center md:text-left md:border-l md:border-white/15 md:pl-6 lg:pl-8"
              data-testid={`stat-${idx}`}
            >
              <div
                className="font-serif text-white tabular-nums tracking-tight leading-none"
                style={{ fontSize: 'clamp(3rem, 6.5vw, 5.5rem)' }}
              >
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="text-[11px] md:text-xs tracking-[0.25em] uppercase text-[#C9A84C] mt-4 font-semibold">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
